import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, TextField, Button, CircularProgress, Alert, Container,
    FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Checkbox, FormGroup,
    Accordion, AccordionSummary, AccordionDetails, FormHelperText, Grid, Link
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorIcon from '@mui/icons-material/Error';
import api from './api';
import config from './config';

// Helper to get section instructions, using ONLY the map with processed keys
const getSectionInstructions = (processedSectionKey, userName) => {
    const specificInstructionsMap = {
        'behavioral_communication_style_prs': `For each statement pair below, please select the one statement that is most like ${userName}. Please select only one statement from each pair. If neither statement is a perfect fit, select the one that is closest to how you perceive ${userName}. A response for every statement pair in this section are required.\n\nIn a professional setting, ${userName}...`, // Added new sentence here
        'adjectives_selection_prs': `From the list below, please select words or phrases that you believe best describe ${userName} in professional environments. This question is required; please select at least one.`,
        'comm_methods_selection_prs': `Please select all of the methods by which you typically communicate with ${userName}. If you use a method not listed, please select "Other" and specify. This question is required.`,
        'formative_reinforcing_feedback_prs': `The following two questions are designed to provide ${userName} with feedback about how they can improve (formative feedback) and what they should continue doing to cultivate a strong reputation (reinforcing feedback). The first question is required while the second question is optional and allows you to provide any additional feedback not addressed by any of your other responses. Please be candid and specific; your responses are anonymous.`
    };
    return specificInstructionsMap[processedSectionKey] || ''; 
};

// Helper to get accordion titles, mapping the section_key we will use in the processed state
const getAccordionTitle = (processedSectionKey, fallbackTitleFromAPI) => {
    const titlesMap = {
        'behavioral_communication_style_prs': "Behavioral Communication Style",
        'adjectives_selection_prs': "Adjectives",
        'comm_methods_selection_prs': "Communication Methods",
        'formative_reinforcing_feedback_prs': "Formative and Reinforcing Feedback"
    };
    return titlesMap[processedSectionKey] || fallbackTitleFromAPI; 
};


function ProfessionalReputationSurvey() {
    const { uniqueSurveyToken } = useParams();
    // const navigate = useNavigate(); // Not currently used

    const [surveyUserName, setSurveyUserName] = useState('');
    const [pinRequired, setPinRequired] = useState(false);
    const [pinVerified, setPinVerified] = useState(false);
    const [enteredPin, setEnteredPin] = useState('');

    const [questionsLoading, setQuestionsLoading] = useState(true);
    const [surveySections, setSurveySections] = useState([]);
    const [pageError, setPageError] = useState('');
    const [pinError, setPinError] = useState('');

    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const [prsSessionToken, setPrsSessionToken] = useState(localStorage.getItem(`prs_session_${uniqueSurveyToken}`) || null);
    const [responses, setResponses] = useState({});
    const [pageLoading, setPageLoading] = useState(true);
    const [validationErrors, setValidationErrors] = useState({});
    const [expandedAccordion, setExpandedAccordion] = useState(null);


    useEffect(() => {
        setPageError(''); setPageLoading(true);
        api.get(`/api/public/prs/survey_details/${uniqueSurveyToken}`)
            .then(response => {
                setSurveyUserName(response.data.user_name_for_survey);
                setPinRequired(response.data.pin_required);
                if (!response.data.pin_required) {
                    setPinVerified(true); 
                }
            })
            .catch(err => {
                setPageError(err.response?.data?.message || "Failed to load survey details. The link may be invalid, expired, or the survey may no longer be active.");
            })
            .finally(() => setPageLoading(false));
    }, [uniqueSurveyToken]);

    const processAndSetSurveySections = (fetchedSectionsOriginal) => {
        const apiSections = JSON.parse(JSON.stringify(fetchedSectionsOriginal));
        
        const formativeItemKey = 'formative_q1'; 
        const reinforcingItemKey = 'reinforcing_q1';

        let formativeQuestion = null;
        let reinforcingQuestion = null;
        let formativeDisplayOrder = Infinity;

        const processedSectionsOutput = [];

        apiSections.forEach(section => {
            section.questions.forEach(q => {
                if (q.item_key === formativeItemKey) {
                    formativeQuestion = { ...q, original_section_key: section.section_key };
                    formativeDisplayOrder = q.display_order !== undefined ? q.display_order : formativeDisplayOrder;
                } else if (q.item_key === reinforcingItemKey) {
                    reinforcingQuestion = { ...q, original_section_key: section.section_key };
                }
            });
        });
        
        apiSections.forEach(apiSection => {
            let internalSectionKey = apiSection.section_key; 
            if (apiSection.section_key === "BEHAVIORAL_STYLE") internalSectionKey = 'behavioral_communication_style_prs';
            else if (apiSection.section_key === "ADJECTIVES") internalSectionKey = 'adjectives_selection_prs';
            else if (apiSection.section_key === "COMM_METHODS") internalSectionKey = 'comm_methods_selection_prs';

            if (formativeQuestion && reinforcingQuestion) {
                if (apiSection.section_key === "FORMATIVE_FEEDBACK" || apiSection.section_key === "REINFORCING_FEEDBACK") {
                    return; 
                }
            }
            
            let questionsForThisSection = apiSection.questions;
            if (formativeQuestion && reinforcingQuestion) {
                questionsForThisSection = apiSection.questions.filter(
                    q => q.item_key !== formativeItemKey && q.item_key !== reinforcingItemKey
                );
            }
            
            if (questionsForThisSection.length > 0 || apiSection.questions.length === 0 ) {
                 processedSectionsOutput.push({
                    ...apiSection, 
                    section_key: internalSectionKey, 
                    questions: questionsForThisSection, 
                    display_order: questionsForThisSection.length > 0 ? (questionsForThisSection[0].display_order || Infinity) : (apiSection.questions.length > 0 ? apiSection.questions[0].display_order : Infinity)
                });
            }
        });

        if (formativeQuestion && reinforcingQuestion) {
            processedSectionsOutput.push({
                section_key: 'formative_reinforcing_feedback_prs',
                section_title: 'Formative and Reinforcing Feedback', 
                questions: [formativeQuestion, reinforcingQuestion].sort((a,b) => (a.display_order || 0) - (b.display_order || 0)),
                display_order: formativeDisplayOrder, 
            });
        }
        
        processedSectionsOutput.sort((a, b) => (a.display_order || Infinity) - (b.display_order || Infinity));
        
        setSurveySections(processedSectionsOutput);

        const initialResponses = {};
        processedSectionsOutput.forEach(section => {
            (section.questions || []).forEach(q => {
                if (q.question_type_prs === 'MULTI_SELECT_UNLIMITED' || q.question_type_prs === 'MULTI_SELECT_WITH_OTHER') {
                    initialResponses[q.question_id] = [];
                } else { initialResponses[q.question_id] = ''; }
                if (q.question_type_prs === 'MULTI_SELECT_WITH_OTHER' && q.options?.find(opt => opt.is_other_trigger)) {
                    initialResponses[`${q.question_id}_other_text`] = '';
                }
            });
        });
        setResponses(initialResponses);

        if (processedSectionsOutput.length > 0) {
            setExpandedAccordion(processedSectionsOutput[0].section_key); 
        }
    };

    useEffect(() => {
        if (pinVerified && (prsSessionToken || !pinRequired)) {
            setQuestionsLoading(true); setPageError('');
            const headers = prsSessionToken ? { Authorization: `Bearer ${prsSessionToken}` } : {};

            api.get(`/api/public/prs/survey/${uniqueSurveyToken}/questions`, { headers })
                .then(response => {
                    const fetchedSections = response.data.sections || [];
                    processAndSetSurveySections(fetchedSections);
                })
                .catch(err => {
                    setPageError(err.response?.data?.message || "Failed to load survey questions. Please ensure the PIN was correct if one was required, or try again later.");
                })
                .finally(() => setQuestionsLoading(false));
        }
    }, [pinVerified, uniqueSurveyToken, prsSessionToken, pinRequired]);

    const handlePinSubmit = async (e) => {
        e.preventDefault();
        setPinError(''); setPageLoading(true);
        try {
            const response = await api.post(`/api/public/prs/survey/${uniqueSurveyToken}/verify_pin`, { pin: enteredPin });
            if (response.data.verified) {
                setPinVerified(true);
                setPrsSessionToken(response.data.prs_session_token);
                localStorage.setItem(`prs_session_${uniqueSurveyToken}`, response.data.prs_session_token);
                setSurveyUserName(response.data.user_name_for_survey);
            } else {
                setPinError(response.data.message || "Invalid PIN.");
            }
        } catch (err) {
            setPinError(err.response?.data?.message || "Error verifying PIN.");
        } finally {
            setPageLoading(false);
        }
    };

    const handleResponseChange = useCallback((questionId, questionType, value, optionId = null, isOtherText = false) => {
        setResponses(prevResponses => {
            const newResponsesState = { ...prevResponses };
            const question = surveySections
                .flatMap(s => s.questions)
                .find(q => q.question_id === questionId);

            if (!question) return prevResponses;

            if (isOtherText) {
                newResponsesState[`${questionId}_other_text`] = value;
            } else if (questionType === 'MULTI_SELECT_UNLIMITED' || questionType === 'MULTI_SELECT_WITH_OTHER') {
                let currentSelection = prevResponses[questionId] ? [...prevResponses[questionId]] : [];
                if (value) { 
                    if (optionId !== null && !currentSelection.includes(optionId)) {
                        currentSelection.push(optionId);
                    }
                } else { 
                    if (optionId !== null) {
                        currentSelection = currentSelection.filter(id => id !== optionId);
                    }
                }
                newResponsesState[questionId] = currentSelection;
            } else {
                newResponsesState[questionId] = value;
            }
            return newResponsesState;
        });
        setValidationErrors(prevErrors => {
            const nextErrors = { ...prevErrors };
            delete nextErrors[questionId];
            if (questionType === 'MULTI_SELECT_WITH_OTHER' && !isOtherText) {
                delete nextErrors[`${questionId}_other_text`];
            }
            return nextErrors;
        });
    }, [surveySections]);

    const validateSection = useCallback((sectionKeyToValidate) => {
        const section = surveySections.find(s => s.section_key === sectionKeyToValidate);
        if (!section || !Array.isArray(section.questions)) {
            return { isValid: true, errors: {} };
        }
        let allValidInStep = true;
        const stepErrors = {};

        for (const q of section.questions) {
            if (!q || typeof q.question_id === 'undefined') continue;

            const response = responses[q.question_id];
            let isValid = true;
            let errorMsg = null;
            
            if (q.item_key === 'reinforcing_q1' && q.question_type_prs === 'OPEN_ENDED_OPTIONAL') {
                isValid = true; 
            } else if (q.question_type_prs === 'SELECT_ONE_FROM_PAIR_SCORED') {
                if (response === '' || response === null || response === undefined) {
                    isValid = false; errorMsg = "Please select one option for each pair.";
                }
            } else if (q.item_key === 'adjectives_selection_prs' && q.question_type_prs === 'MULTI_SELECT_UNLIMITED') { 
                if (!Array.isArray(response) || response.length === 0) {
                    isValid = false; errorMsg = "Please select at least one adjective.";
                }
            } else if (q.item_key === 'comm_methods_selection' && q.question_type_prs === 'MULTI_SELECT_WITH_OTHER') { 
                const otherOption = q.options?.find(opt => opt.is_other_trigger);
                const otherText = responses[`${q.question_id}_other_text`]?.trim();
                const mainSelectionMade = Array.isArray(response) && response.length > 0;
                
                if (!mainSelectionMade && !otherText) {
                    isValid = false; errorMsg = "Please select at least one communication method or specify 'Other'.";
                } else if (otherOption && Array.isArray(response) && response.includes(otherOption.option_id) && !otherText) {
                    isValid = false; errorMsg = "Please specify your 'Other' communication method.";
                    stepErrors[`${q.question_id}_other_text`] = "Please specify here.";
                }
            } else if (q.question_type_prs === 'OPEN_ENDED_REQUIRED') { 
                if (!response || response.trim() === '') {
                    isValid = false; errorMsg = "This field is required.";
                }
            } else if (q.question_type_prs !== 'OPEN_ENDED_OPTIONAL') { 
                 if (response === '' || response === null || response === undefined || (Array.isArray(response) && response.length === 0)) {
                    isValid = false; errorMsg = "This field is required.";
                 }
            }

            if (!isValid) {
                allValidInStep = false;
                stepErrors[q.question_id] = errorMsg;
            }
        }
        return { isValid: allValidInStep, errors: stepErrors };
    }, [responses, surveySections]);

    const handleNextAccordion = (currentSectionIndex) => {
        const currentSectionKey = surveySections[currentSectionIndex].section_key;
        const { isValid, errors } = validateSection(currentSectionKey);
        
        setValidationErrors(prev => {
            const updatedErrors = {...prev};
            surveySections[currentSectionIndex].questions.forEach(q => {
                delete updatedErrors[q.question_id];
                delete updatedErrors[`${q.question_id}_other_text`];
            });
            Object.assign(updatedErrors, errors);
            return updatedErrors;
        });

        if (isValid && currentSectionIndex < surveySections.length - 1) {
            const nextSectionKey = surveySections[currentSectionIndex + 1].section_key;
            setExpandedAccordion(nextSectionKey);
            setTimeout(() => {
                const element = document.getElementById(`${nextSectionKey}-header`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 0);
        } else if (!isValid) {
             setTimeout(() => {
                const element = document.getElementById(`${currentSectionKey}-header`);
                if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 0);
        }
    };

    const handlePreviousAccordion = (currentSectionIndex) => {
        if (currentSectionIndex > 0) {
            const prevSectionKey = surveySections[currentSectionIndex - 1].section_key;
            setExpandedAccordion(prevSectionKey);
            setTimeout(() => {
                const element = document.getElementById(`${prevSectionKey}-header`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 0);
        }
    };

    const handleSubmitSurvey = async () => {
        let allSectionsValid = true;
        const allEncounteredValidationErrors = {};

        for (const section of surveySections) {
            const { isValid, errors } = validateSection(section.section_key);
            if (!isValid) {
                allSectionsValid = false;
                Object.assign(allEncounteredValidationErrors, errors);
            }
        }
        setValidationErrors(allEncounteredValidationErrors);

        if (!allSectionsValid) {
            setSubmitError("Please correct the errors in the highlighted sections before submitting.");
            const firstErrorSectionKey = surveySections.find(section =>
                section.questions.some(q => allEncounteredValidationErrors[q.question_id] || allEncounteredValidationErrors[`${q.question_id}_other_text`])
            )?.section_key;
            if (firstErrorSectionKey) {
                setExpandedAccordion(firstErrorSectionKey);
                 setTimeout(() => {
                    const element = document.getElementById(`${firstErrorSectionKey}-header`);
                    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 0);
            }
            return;
        }
        setSubmitError('');
        setSubmitLoading(true);

        const formattedAnswers = [];
        (surveySections || []).forEach(section => {
            (section.questions || []).forEach(q => {
                const answer = { prs_question_id: q.question_id };
                const responseValue = responses[q.question_id];
                let hasValue = false;

                if (q.question_type_prs === 'SELECT_ONE_FROM_PAIR_SCORED') {
                    if (responseValue) { answer.selected_option_id = parseInt(responseValue); hasValue = true; }
                } else if (q.question_type_prs === 'MULTI_SELECT_UNLIMITED' || q.question_type_prs === 'MULTI_SELECT_WITH_OTHER') {
                    if (Array.isArray(responseValue) && responseValue.length > 0) {
                        answer.selected_option_ids = responseValue.map(id => parseInt(id));
                        hasValue = true;
                    }
                    const otherOption = q.options?.find(opt => opt.is_other_trigger);
                    if (otherOption && Array.isArray(responseValue) && responseValue.includes(otherOption.option_id)) {
                        const otherText = responses[`${q.question_id}_other_text`];
                        if (otherText && otherText.trim() !== '') {
                            answer.answer_text_value = otherText.trim();
                            hasValue = true; 
                        }
                    }
                } else if (q.question_type_prs.startsWith('OPEN_ENDED')) { 
                    if (responseValue && responseValue.trim() !== '') { 
                        answer.answer_text_value = responseValue.trim(); hasValue = true; 
                    } else if (q.question_type_prs === 'OPEN_ENDED_REQUIRED') { 
                        answer.answer_text_value = ""; hasValue = true; 
                    }
                }
                
                if (hasValue) {
                    formattedAnswers.push(answer);
                }
            });
        });
        
        try {
            await api.post(`/api/public/prs/survey/${uniqueSurveyToken}/submit`,
                { answers: formattedAnswers },
                { headers: { Authorization: `Bearer ${prsSessionToken}` } }
            );
            setSubmitSuccess(true);
            localStorage.removeItem(`prs_session_${uniqueSurveyToken}`);
        } catch (err) {
            setSubmitError(err.response?.data?.message || "Failed to submit survey. Please ensure all required fields are completed.");
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleAccordionChange = (sectionKey) => (event, isExpanded) => {
        setExpandedAccordion(isExpanded ? sectionKey : false);
    };

    // --- Render Logic ---
    if (pageLoading && !surveyUserName) return <Container sx={{ textAlign: 'center', mt: 5 }}><CircularProgress size="large" /></Container>;
    if (pageError && !surveyUserName) return <Container><Alert severity="error" sx={{ mt: 3, p: 2, fontSize: '1.1rem' }}>{pageError}</Alert></Container>;

    if (submitSuccess) {
        return (
            <Container maxWidth="sm" sx={{ textAlign: 'center', py: {xs: 2, sm: 4} }}>
                <Paper elevation={3} sx={{ p: {xs: 2, sm: 3} }}>
                    <Typography variant="h4" gutterBottom sx={{fontSize: {xs: '1.8rem', sm: '2.2rem'}}}>Thank You!</Typography>
                    <Typography sx={{fontSize: {xs: '1rem', sm: '1.2rem'}}}>Your feedback for {surveyUserName} has been submitted successfully.</Typography>
                </Paper>
            </Container>
        );
    }

    if (pinRequired && !pinVerified) {
        const introText = `Thank you for taking the time to complete this brief survey about ${surveyUserName || 'this individual'}. All but the very last question are required. Your responses are anonymous and will help the individual better understand how they are perceived in professional environments. This understanding will help them bring their actual reputation into closer alignment with their ideal or intended reputation.`;
        return (
            <Container maxWidth="sm" sx={{ textAlign: 'center', py: {xs: 2, sm: 4} }}>
                <Paper elevation={3} sx={{ p: {xs: 2, sm: 3} }}>
                    <Typography variant="h5" gutterBottom sx={{fontSize: {xs: '1.5rem', sm: '2rem'}}}>
                        Professional Reputation Survey for {surveyUserName || 'this user'}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, textAlign: 'left', fontSize: {xs: '0.9rem', sm: '1.1rem'}, whiteSpace: 'pre-line' }}>
                        {introText}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, textAlign: 'left', fontSize: {xs: '0.9rem', sm: '1.1rem'} }}>
                        Please enter the PIN to access this survey.
                    </Typography>
                    <form onSubmit={handlePinSubmit}>
                        <TextField
                            label="Survey PIN"
                            type="password"
                            value={enteredPin}
                            onChange={(e) => setEnteredPin(e.target.value)}
                            variant="outlined"
                            size="medium" 
                            fullWidth
                            sx={{ mb: 1 }}
                            InputProps={{ style: { fontSize: '1.1rem' } }}
                            InputLabelProps={{ style: { fontSize: '1.1rem' } }}
                        />
                        {pinError && <Alert severity="error" sx={{ mb: 2, textAlign: 'left', fontSize: '1rem' }}>{pinError}</Alert>}
                        <Button type="submit" variant="contained" disabled={pageLoading} size="large" sx={{fontSize: '1.1rem'}}>
                            {pageLoading ? <CircularProgress size={28} /> : "Verify PIN"}
                        </Button>
                    </form>
                </Paper>
            </Container>
        );
    }

    if (questionsLoading) return <Container sx={{ textAlign: 'center', mt: 5 }}><CircularProgress size="large" /></Container>;
    if (pageError) return <Container><Alert severity="error" sx={{ mt: 3, p: 2, fontSize: '1.1rem' }}>{pageError}</Alert></Container>;
    if (!surveySections.length && pinVerified) return <Container><Alert severity="info" sx={{ mt: 3, p: 2, fontSize: '1.1rem' }}>No survey questions found, or the survey is not yet available after PIN verification.</Alert></Container>;
    if (!pinVerified && pinRequired) return null;

    return (
        <Container maxWidth="lg" sx={{ py: {xs:1, sm:3} }}>
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h4" component="h1" gutterBottom align="center" sx={{fontSize: {xs: '1.8rem', sm: '2.5rem'}, mb:3}}>
                    Professional Reputation Survey for {surveyUserName}
                </Typography>

                {submitError && <Alert severity="error" sx={{ mb: 3, p: 2, fontSize: '1.1rem' }}>{submitError}</Alert>}

                {surveySections.map((section, index) => {
                    const sectionHasError = section.questions.some(q => validationErrors[q.question_id] || validationErrors[`${q.question_id}_other_text`]);
                    const accordionTitle = getAccordionTitle(section.section_key, section.section_title); 
                    const sectionInstructionsText = getSectionInstructions(section.section_key, surveyUserName); // Simplified call
                    
                    // Determine if question text should be displayed for this question
                    // Only hide for questions within "Adjectives" and "Communication Methods" sections
                    const hideQuestionTextForSection = section.section_key === 'adjectives_selection_prs' || section.section_key === 'comm_methods_selection_prs';

                    return (
                        <Accordion
                            key={section.section_key || `section-${index}`} 
                            expanded={expandedAccordion === section.section_key}
                            onChange={handleAccordionChange(section.section_key)}
                            sx={{ mb: 1.5, '&:before': { display: 'none' }, border: sectionHasError ? '2px solid red' : '1px solid rgba(0,0,0,0.12)' }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`${section.section_key}-content`}
                                id={`${section.section_key}-header`} 
                                sx={{ backgroundColor: sectionHasError ? 'rgba(255,0,0,0.05)' : 'transparent' }}
                            >
                                <Box sx={{display: 'flex', alignItems: 'center', width: '100%'}}>
                                    {sectionHasError && <ErrorIcon color="error" sx={{ mr: 1 }} />}
                                    <Typography variant="h6" sx={{ flexGrow: 1, fontSize: {xs: '1.1rem', sm: '1.3rem'} }}>{accordionTitle}</Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: {xs: 1.5, sm:2.5} }}>
                                {sectionInstructionsText && (
                                    <Box sx={{ mb: 2.5, p: {xs: 1, sm:2}, backgroundColor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                                        <Typography variant="body1" sx={{ textAlign: 'left', whiteSpace: 'pre-line', fontSize: {xs: '1rem', sm: '1.1rem'} }}>
                                            {sectionInstructionsText}
                                        </Typography>
                                    </Box>
                                )}

                                {(section.questions || []).map(q => (
                                    <Box key={q.question_id} sx={{ mb: 3, textAlign: 'left', pl: validationErrors[q.question_id] ? 0.8 : 0, pt:0.5, borderLeft: validationErrors[q.question_id] ? '3px solid red' : 'none', backgroundColor: validationErrors[q.question_id] ? 'rgba(255,0,0,0.03)' : 'transparent', borderRadius: validationErrors[q.question_id] ? '4px' : 0 }}>
                                        {/* Conditionally render question text */}
                                        {!hideQuestionTextForSection && (
                                            <Typography variant="subtitle1" sx={{ textAlign: 'left', mb: 1, whiteSpace: 'pre-line', fontWeight: q.question_type_prs === 'SELECT_ONE_FROM_PAIR_SCORED' ? 'bold' : 'normal', fontSize: {xs: '1rem', sm: '1.2rem'} }}>
                                                {q.question_text}
                                            </Typography>
                                        )}

                                        {q.question_type_prs === 'SELECT_ONE_FROM_PAIR_SCORED' && (
                                            <FormControl component="fieldset" error={!!validationErrors[q.question_id]} sx={{ ml: q.question_text.startsWith('Pair') && !hideQuestionTextForSection ? 2 : 0, width: '100%' }}>
                                                <RadioGroup name={`q-${q.question_id}`} value={responses[q.question_id] || ''} onChange={(e) => handleResponseChange(q.question_id, q.question_type_prs, e.target.value)}>
                                                    {(q.options || []).map(opt => (<FormControlLabel key={opt.option_id} value={opt.option_id.toString()} control={<Radio size="medium" />} label={<Typography variant="body1" sx={{ textAlign: 'left', fontSize: {xs: '0.95rem', sm: '1.1rem'} }}>{opt.option_text}</Typography>} />))}
                                                </RadioGroup>
                                                {validationErrors[q.question_id] && <FormHelperText sx={{ml: '14px'}}>{validationErrors[q.question_id]}</FormHelperText>}
                                            </FormControl>
                                        )}
                                        {q.item_key === 'adjectives_selection_prs' && q.question_type_prs === 'MULTI_SELECT_UNLIMITED' && ( 
                                            <FormGroup sx={{ width: '100%' }}>
                                                 <Grid container spacing={1}>
                                                    {(q.options || []).map(opt => (
                                                        <Grid item xs={12} sm={6} md={4} key={opt.option_id}>
                                                            <FormControlLabel
                                                                control={<Checkbox checked={(responses[q.question_id] || []).includes(opt.option_id)} onChange={(e) => handleResponseChange(q.question_id, q.question_type_prs, e.target.checked, opt.option_id)} />}
                                                                label={<Typography variant="body1" sx={{ textAlign: 'left', fontSize: {xs: '0.95rem', sm: '1.1rem'} }}>{opt.option_text}</Typography>}
                                                                sx={{width: '100%'}}
                                                            />
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                                {validationErrors[q.question_id] && <FormHelperText error sx={{ textAlign: 'left', mt:1 }}>{validationErrors[q.question_id]}</FormHelperText>}
                                                {!validationErrors[q.question_id] && <FormHelperText sx={{ textAlign: 'left', mt:1, fontSize: '0.9rem' }}>Please select at least one adjective.</FormHelperText>}
                                            </FormGroup>
                                        )}
                                        {q.item_key === 'comm_methods_selection' && q.question_type_prs === 'MULTI_SELECT_WITH_OTHER' && ( 
                                            <FormGroup sx={{ ml: 0, width: '100%' }}>
                                                {(q.options || []).map(opt => (<Box key={opt.option_id} sx={{mb:0.5}}>
                                                    <FormControlLabel control={<Checkbox checked={(responses[q.question_id] || []).includes(opt.option_id)} onChange={(e) => handleResponseChange(q.question_id, q.question_type_prs, e.target.checked, opt.option_id)} />} label={<Typography variant="body1" sx={{ textAlign: 'left', fontSize: {xs: '0.95rem', sm: '1.1rem'} }}>{opt.option_text.replace('Twitter/X', 'Twitter/X')}</Typography>} />
                                                    {opt.is_other_trigger && (responses[q.question_id] || []).includes(opt.option_id) && (
                                                        <TextField
                                                            label="Please specify other" variant="outlined" size="medium"
                                                            value={responses[`${q.question_id}_other_text`] || ''}
                                                            onChange={(e) => handleResponseChange(q.question_id, q.question_type_prs, e.target.value, opt.option_id, true)}
                                                            sx={{ mt: 0.5, ml: {xs: 0, sm: 4}, width: {xs: '100%', sm: 'calc(100% - 32px)'}, maxWidth: '500px' }}
                                                            error={!!validationErrors[`${q.question_id}_other_text`]}
                                                            helperText={validationErrors[`${q.question_id}_other_text`]}
                                                            InputProps={{ style: { fontSize: '1.1rem' } }}
                                                            InputLabelProps={{ style: { fontSize: '1.1rem' } }}
                                                        />
                                                    )}
                                                </Box>))}
                                                 {validationErrors[q.question_id] && !validationErrors[`${q.question_id}_other_text`] && <FormHelperText error sx={{mt:1}}>{validationErrors[q.question_id]}</FormHelperText>}
                                            </FormGroup>
                                        )}
                                        {(q.question_type_prs === 'OPEN_ENDED_REQUIRED' || q.question_type_prs === 'OPEN_ENDED_OPTIONAL') && (
                                            <TextField
                                                label={q.item_key === 'reinforcing_q1' && q.question_type_prs === 'OPEN_ENDED_OPTIONAL' ? "Your response (optional)" : "Your response (required)"}
                                                multiline rows={5} fullWidth variant="outlined"
                                                value={responses[q.question_id] || ''}
                                                onChange={(e) => handleResponseChange(q.question_id, q.question_type_prs, e.target.value)}
                                                error={!!validationErrors[q.question_id]}
                                                helperText={validationErrors[q.question_id]}
                                                InputProps={{ style: { fontSize: '1.1rem' } }}
                                                InputLabelProps={{ style: { fontSize: '1.1rem' } }}
                                            />
                                        )}
                                    </Box>
                                ))}
                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt:2}}>
                                   {index > 0 ? (
                                        <Link component="button" variant="body2" onClick={() => handlePreviousAccordion(index)} sx={{fontSize: '1rem'}}>
                                            Previous Section
                                        </Link>
                                    ) : <Box sx={{width: '130px'}} /> }

                                    {index < surveySections.length - 1 && (
                                        <Button variant="contained" onClick={() => handleNextAccordion(index)} sx={{fontSize: '1rem'}}>
                                            Next Section
                                        </Button>
                                    )}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Button onClick={handleSubmitSurvey} variant="contained" color="primary" disabled={submitLoading} size="large" sx={{fontSize: '1.2rem', px:4, py:1.5}}>
                        {submitLoading ? <CircularProgress size={28} color="inherit" /> : "Submit Survey"}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default ProfessionalReputationSurvey;
